import { ChangeEvent, useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

// Definimos los tipos de archivo soportados
export type FileType = "csv" | "image";

export interface RequiredColumn {
    key: string;
    translations: string[];
}
export interface AppAttachFileInputProp {
    fileType: FileType;
    onSelectFile: (file: File) => void;
    onClear?: () => void;
    clearTrigger?: number;
    initialFileName?: string;
    requiredColumns?: RequiredColumn[];
    headerCountText?: string;
    totalRowsToImport?: number | null;
    showLabel?: boolean;
    setTotalRowsToImport?: (totalRows: number | null) => void;
}

export default function AppAttachFileInput({
    fileType,
    requiredColumns,
    headerCountText,
    totalRowsToImport = null,
    showLabel = true,
    onSelectFile,
    onClear,
    clearTrigger,
    initialFileName,
    setTotalRowsToImport
}: AppAttachFileInputProp) {

    const { t } = useTranslation(['common']);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFileName, setSelectedFileName] = useState("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setSelectedFileName("");
        setError(null);
        if (setTotalRowsToImport) setTotalRowsToImport(null);
    }, [clearTrigger, initialFileName, setTotalRowsToImport]);

    useEffect(() => {
        setSelectedFileName(initialFileName ?? "");
    }, [initialFileName]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        setError(null);
        if (setTotalRowsToImport) setTotalRowsToImport(null);

        if (!file) return;

        setSelectedFileName(file.name);

        if (fileType === "image") {
            const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
            if (!allowedTypes.includes(file.type)) {
                setError(t("common:form.invalidFormat"));
                return;
            }
            onSelectFile(file);
        }
        else if (fileType === "csv") {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                encoding: "ISO-8859-1",
                complete: (results) => {
                    const fileColumns = results.meta.fields ?? [];
                    const hasRequiredColumns = requiredColumns?.some((_, languageIndex) =>
                        requiredColumns.every(column =>
                            fileColumns.includes(column.translations[languageIndex])
                        )
                    ) ?? true;

                    if (fileColumns.length === 0 || !hasRequiredColumns) {
                        setError(t("common:csv.invalidCsvFormat"));
                        return;
                    }
                    if (setTotalRowsToImport) setTotalRowsToImport(results.data.length);
                    onSelectFile(file);
                },
                error: () => setError(t("common:csv.invalidCsvFormat"))
            });
        }
    };

    return (
        <Form.Group>
            {showLabel &&
                <Form.Label>
                    {fileType === "csv" ? t("common:csv.selectFile") : t("common:form.selectImage")}
                </Form.Label>
            }
            <div className="d-flex align-items-center gap-2 w-100">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={fileType === "csv" ? ".csv" : "image/png, image/jpeg"}
                    onChange={handleFileChange}
                    className="d-none"
                />
                <Form.Control
                    value={selectedFileName || t("common:csv.noFileSelected")}
                    readOnly
                    className="flex-grow-1"
                />
                {selectedFileName && (
                    <Button
                        type="button"
                        variant="link"
                        className="p-0 text-danger text-decoration-none"
                        aria-label={t("common:buttons.buttonDelete")}
                        onClick={() => {
                            setSelectedFileName("");
                            onClear?.();
                        }}
                    >
                        <i className="bi bi-x-lg"></i>
                    </Button>
                )}
                <Button
                    variant="secondary"
                    className="ms-auto flex-shrink-0 text-nowrap"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                >
                    {t("common:csv.selectFileButton")}
                </Button>
            </div>
            
            {error && <small className="text-danger">{error}</small>}
            
            {fileType === "csv" && totalRowsToImport !== null && headerCountText && (
                <small className="text-muted">
                    {t(headerCountText, { countValues: totalRowsToImport })}
                </small>
            )}
        </Form.Group>
    );
}
